/* eslint-disable complexity */
import React from "react";
import queryString from "query-string";
import "./styles.scss";
import Logo from "../../assets/logo-white-2.png";
import CustomLogin from "../CustomLogin";
import getTestKeyInfo from "../../api/getTestKeyInfo";

const { key: testKey } = queryString.parse(window.location.search);

const styles = {
  errorMessage: { color: "yellow", fontSize: 18, overflowWrap: "break-word" },
  errorTitle: {
    fontWeight: "bold",
    color: "#ff6600",
    textShadow: "black 1px 1px 1px",
    fontSize: 20,
    marginRight: 3,
    overflowWrap: "break-word",
  },
};

export default class Login extends React.Component {
  state = {
    bootstrapping: true,
    email: null,
    pinCode: null,
    childSelfStarting: null,

    requiresVerification: null,
    instructions: null,
    dispatchAddress: null,
    altError: null,

    // UI
    showPinLogin: false,

    // reuse banner
    reuseSignal: null,
    showReuseBanner: false,
  };

  componentDidMount() {
    localStorage.removeItem("authtoken-screening");
    if (!testKey) {
      window.location.href = "/?error=true";
      return;
    }

    getTestKeyInfo(testKey)
      .then((response) => {
        if (!response) throw new Error("No response");

        const {
          email: rawEmail,
          initialPin,
          dispatchAddress,
          childSelfStarting,
          requiresVerification,
          isDistributed,
          isNorming,
          status,
          reuseSignal,
        } = response;

        const email = rawEmail || dispatchAddress;

        // Build German instruction text (only if verification is needed)
        let instructionsText = null;
        if (status !== "isCompleted" && requiresVerification) {
          instructionsText = (
            <p className="lit-verify-note">
              <span>
                Diese Prüfung ist älter als ein Monat. Um Ihre Privatsphäre zu
                schützen, müssen wir bestätigen, dass die richtige Person den
                Test startet. Bitte verwenden Sie den an{" "}
                <span style={{ color: "white" }}>
                  <i>
                    <u>{dispatchAddress}</u>
                  </i>
                </span>{" "}
                gesendeten <b>PIN-Code</b>, um zu beginnen.
              </span>
            </p>
          );
        }
        if (status === "isUsed") {
          instructionsText = (
            <p className="lit-verify-note">
              <span>
                Dieser Test wurde bereits begonnen, aber nicht abgeschlossen. Aus Datenschutzgründen stellen wir sicher, dass die richtige Person fortfährt. Bitte verwenden Sie den an{" "}
                <span style={{ color: "white" }}>
                  <i>
                    <u>{dispatchAddress}</u>
                  </i>
                </span>{" "}
                gesendeten <b>PIN</b>, um fortzufahren.
              </span>
            </p>
          );
        }

        const near = Boolean(reuseSignal && reuseSignal.nearSimultaneous);

        this.setState({
          email,
          pinCode: initialPin,
          childSelfStarting,
          requiresVerification,
          dispatchAddress,
          instructions: instructionsText,
          altError: status === "isCompleted" ? "Dieser Test wurde bereits abgeschlossen." : null,
          reuseSignal,
          showReuseBanner: near,
        });
      })
      .catch(async (resp) => {
        let msg = "Ungültiger Testschlüssel oder bereits verwendet.";
        try {
          const j = await resp?.json();
          if (j?.error === "pincode instead") {
            msg = "Es sieht so aus, als würden Sie einen PIN-Code statt eines Testschlüssels verwenden.";
          }
        } catch (_) {}
        this.setState({ altError: msg });
      })
      .finally(() => this.setState({ bootstrapping: false }));
  }

  dismissReuseBanner = () => this.setState({ showReuseBanner: false });

  formatSince(mins) {
    if (typeof mins !== "number" || mins < 0) return "";
    if (mins < 1) return "soeben";
    if (mins === 1) return "vor 1 Minute";
    if (mins < 60) return `vor ${mins} Minuten`;
    return "vor kurzer Zeit";
  }

  // show PIN login (we already have email/pin from getTestKeyInfo)
  startPinFlow = () => this.setState({ showPinLogin: true });

  renderBanner() {
    const { showReuseBanner, reuseSignal } = this.state;
    if (!showReuseBanner) return null;

    return (
      <div className="lit-reuse-banner" role="status" aria-live="polite">
        <div className="lit-reuse-banner__dot" aria-hidden="true" />
        <div>
          <strong>Achtung:</strong> Dieser Testschlüssel wurde kürzlich
          verwendet
          {typeof reuseSignal?.minutesSinceUsed === "number" && (
            <> ({this.formatSince(reuseSignal.minutesSinceUsed)})</>
          )}
          .
          <br />
          <span>
            Bitte stellen Sie sicher, dass <u>die richtige Kandidatin / der richtige Kandidat</u> den begonnenen Test fortsetzt.
            Für eine <u>neue Person</u> sollte eine neue Testnummer vergeben werden.
          </span>
        </div>
        <button
          type="button"
          onClick={this.dismissReuseBanner}
          className="lit-reuse-banner__close"
          aria-label="Hinweis ausblenden"
          title="Ausblenden"
        >
          ×
        </button>
      </div>
    );
  }

  renderErrorOrInstructions() {
    const { altError, instructions } = this.state;
    if (altError) {
      return (
        <div style={{ marginTop: 8 }}>
          <span className="error" style={styles.errorMessage}>
            <span style={styles.errorTitle}>
              <i>Fehler: </i>
            </span>
            {altError}
          </span>
        </div>
      );
    }
    if (instructions) {
      return (
        <div style={{ marginTop: 8 }}>
          <span className="error" style={styles.errorMessage}>
            <span style={styles.errorTitle} />
            {instructions}
          </span>
        </div>
      );
    }
    return null;
  }

  render() {
    const {
      bootstrapping,
      requiresVerification,
      showPinLogin,
      email,
      pinCode,
      childSelfStarting,
    } = this.state;

    const showCustomLogin = showPinLogin || (requiresVerification && email);

    return (
      <div className="Login">
        <header className="lit-header">
          <h1 className="lit-logo">
            <img className="lit-logo__img" src={Logo} alt="Literate Logo" />
          </h1>
        </header>

        <div className="lit-wrapper--article text-center">
          <p>
            Melde Dich mit Deiner <b>PIN</b> an, um den Test zu starten.
          </p>

          {this.renderBanner()}

          {!bootstrapping && !requiresVerification && !showPinLogin && (
            <div className="bank-id-type-selector-wrapper" style={{ marginTop: 12 }}>
              <button
                className="lit-btn lit-btn--small bg-secondary"
                onClick={this.startPinFlow}
              >
                PIN-Code
              </button>
            </div>
          )}

          {this.renderErrorOrInstructions()}

          {showCustomLogin && (
            <div className="lit-customlogin-sub-wrapper">
              <CustomLogin
                email={email}
				dispatchAddress={this.state.dispatchAddress}
                pinCode={pinCode}
                childSelfStarting={childSelfStarting}
              />
            </div>
          )}
        </div>
      </div>
    );
  }
}
